/**
 * Abstract base class for toggle-mode skills with active state and cooldown management.
 * Subclasses implement activation logic, break conditions, and deactivation behavior.
 * The base handles cooldown ticking and the active/inactive state lifecycle.
 */
import type { SkillEffect } from './SkillEffect';

/** Base class for skills with an active/inactive state and cooldown tick. */
export abstract class ToggleSkillEffect implements SkillEffect {
  readonly type = 'active' as const;
  readonly activationMode: 'toggle' | 'instant' = 'toggle';
  abstract readonly skillId: string;

  protected _isActive = false;
  protected cooldownRemaining = 0;

  get cooldown(): number { return this.cooldownRemaining; }

  isActive(): boolean { return this._isActive; }

  getBlockReason(): string | null {
    if (this.cooldownRemaining > 0) return 'cooldown';
    return 'not ready';
  }

  /** Deactivate the skill. Override for custom deactivation behavior. */
  deactivate(): void {
    if (!this._isActive) return;
    this._isActive = false;
    this.onDeactivate();
  }

  update(delta: number): void {
    if (this.cooldownRemaining > 0) {
      this.cooldownRemaining = Math.max(0, this.cooldownRemaining - delta);
    }
    if (this._isActive) {
      this.updateWhileActive(delta);
    }
  }

  destroy(): void {
    this._isActive = false;
  }

  // --- Abstract: subclass must implement ---

  abstract canActivate(): boolean;
  abstract activate(): boolean;

  /** Called when deactivate() transitions from active to inactive. */
  protected abstract onDeactivate(): void;

  /** Per-frame logic while the skill is active (break checks, sustained effects, etc). */
  protected abstract updateWhileActive(delta: number): void;
}
